import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Company, CompanyStatus } from '@/features/multi-company/types/company.types';
import { companyService } from '@/features/multi-company/services/companyService';
import { mockCompanyService } from '@/data/mockCompanies';
import { getStoredCompany, setStoredCompany, companyStorage } from '@/features/multi-company/utils/company-storage';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { updateCompanyId } from '@/lib/auth-fetch';

interface CompanyContextType {
  currentCompany: Company | null;
  availableCompanies: Company[];
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
  employeeId?: string; // Optional - defaults to 'current-user'
}

export function CompanyProvider({ children, employeeId = 'current-user' }: CompanyProviderProps) {
  const { isDemo } = useScreenDataMode('integrations');
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let activeCompanies: Company[];

      if (isDemo) {
        const mockCompanies = await mockCompanyService.getCompanies();
        activeCompanies = mockCompanies
          .filter(c => c.status === 'active')
          .map((c) => ({
            id: c.id,
            name: c.name,
            code: c.code,
            status: c.status as CompanyStatus,
            description: '',
            productInstances: [],
            financialIntegration: null,
            hrIntegration: null,
            activeProjects: c.activeProjects,
            employeeCount: c.employeeCount,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastSync: null,
          }));
      } else {
        activeCompanies = await companyService.getActiveCompanies();
      }

      setAvailableCompanies(activeCompanies);

      let companyToLoad: Company | null = null;

      const storedCompanyId = getStoredCompany();
      if (storedCompanyId) {
        companyToLoad = activeCompanies.find((c) => c.id === storedCompanyId) || null;
      }

      if (!companyToLoad && activeCompanies.length > 0) {
        companyToLoad = activeCompanies[0];
      }

      if (companyToLoad) {
        setCurrentCompany(companyToLoad);
        setStoredCompany(companyToLoad.id);
        updateCompanyId(companyToLoad.id);
      } else {
        setError('No companies available');
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [isDemo]);

  // Load companies on mount and when mode changes
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  async function switchCompany(companyId: string) {
    try {
      setIsLoading(true);
      setError(null);

      const company = availableCompanies.find((c) => c.id === companyId);
      if (!company) {
        throw new Error('Company not found or access denied');
      }

      // Clear cache for the old company before switching to ensure fresh data
      if (currentCompany) {
        companyStorage.clearCompanyCache(currentCompany.id);
      }

      setCurrentCompany(company);
      setStoredCompany(companyId);
      updateCompanyId(companyId);

      // Dispatch company-changed event to notify all data-fetching components
      // This ensures that components refetch data using the new company's integration configuration
      window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
    } catch (err) {
      console.error('Failed to switch company:', err);
      setError('Failed to switch company');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function refreshCompanies() {
    await loadCompanies();
  }

  const value: CompanyContextType = {
    currentCompany,
    availableCompanies,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
